import { Button } from '@/components/ui/button';
import { resolveUserName } from '@/features/users/resolveUserName';
import { useUserMap } from '@/features/users/users.hooks';
import { relativeTime } from '@/lib/time';
import { cn } from '@/lib/utils';
import type { Feedback, UUID } from '@certhon/models';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Megaphone,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useListContext } from '@/features/lists/context';
import {
  useFeedbackForList,
  useMarkFeedbackHandledMutation,
} from '../feedback.queries';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FeedbackReviewSectionProps {
  protoListId: UUID;
  protoNodeId: UUID;
}

// ---------------------------------------------------------------------------
// FeedbackReviewSection
// ---------------------------------------------------------------------------

/**
 * Collapsible section shown on the template side (edit mode) that displays
 * feedback items submitted by technicians for a specific template question.
 * Admins can mark feedback as handled from here.
 */
export function FeedbackReviewSection({
  protoListId,
  protoNodeId,
}: FeedbackReviewSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const { user } = useListContext();
  const userMap = useUserMap('recnum');

  const { data: allFeedback, isLoading } = useFeedbackForList(protoListId);
  const markHandled = useMarkFeedbackHandledMutation();

  // Filter feedback for this specific node
  const nodeFeedback = useMemo(() => {
    if (!allFeedback) return [];
    return allFeedback.filter((f) => f.proto_node_id === protoNodeId);
  }, [allFeedback, protoNodeId]);

  const unhandledCount = useMemo(
    () => nodeFeedback.filter((f) => !f.handled).length,
    [nodeFeedback],
  );

  // Don't render if loading or no feedback exists for this node
  if (isLoading || nodeFeedback.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 border-t pt-4">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="text-muted-foreground hover:text-foreground flex w-full items-center gap-2
          text-sm font-medium transition-colors"
      >
        <Megaphone className="h-4 w-4" />
        <span className="flex-1 text-left">Feedback</span>
        {unhandledCount > 0 && (
          <span
            className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-100
              px-1.5 text-xs font-semibold text-orange-700"
          >
            {unhandledCount}
          </span>
        )}
        {expanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="mt-3 space-y-3">
          {nodeFeedback.map((feedback) => (
            <FeedbackReviewItem
              key={feedback.id}
              feedback={feedback}
              reporterName={resolveUserName(feedback.user_id, userMap)}
              handlerName={
                feedback.handled_user_id
                  ? resolveUserName(feedback.handled_user_id, userMap)
                  : null
              }
              onMarkHandled={() =>
                markHandled.mutate({
                  id: feedback.id,
                  handledUserId: user.recnum,
                })
              }
              isPending={markHandled.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeedbackReviewItem
// ---------------------------------------------------------------------------

interface FeedbackReviewItemProps {
  feedback: Feedback;
  reporterName: string;
  handlerName: string | null;
  onMarkHandled: () => void;
  isPending: boolean;
}

function FeedbackReviewItem({
  feedback,
  reporterName,
  handlerName,
  onMarkHandled,
  isPending,
}: FeedbackReviewItemProps) {
  const timeAgo = relativeTime(feedback.timestamp);

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        feedback.handled
          ? 'border-border/50 bg-muted/30'
          : 'border-orange-200 bg-orange-50/50',
      )}
    >
      {/* Header: reporter + timestamp */}
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-foreground text-xs font-medium">
          {reporterName}
        </span>
        <span className="text-muted-foreground text-xs">{timeAgo}</span>
      </div>

      {/* Message */}
      <p className="text-foreground/90 text-sm">{feedback.message}</p>

      {/* Footer: handled status or action */}
      <div className="mt-2 flex items-center justify-between gap-2">
        {feedback.instance_list_id && (
          <span className="text-muted-foreground text-xs">
            From checklist instance
          </span>
        )}

        {feedback.handled ? (
          <span className="ml-auto flex items-center gap-1 text-xs text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Handled{handlerName ? ` by ${handlerName}` : ''}
          </span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto h-7 text-xs"
            onClick={onMarkHandled}
            disabled={isPending}
          >
            {isPending ? 'Saving...' : 'Mark as handled'}
          </Button>
        )}
      </div>
    </div>
  );
}
